import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Globe, MapPin, Check, X, AlertTriangle, Save, Plus } from 'lucide-react';
import { useFlashMessage } from '../components/FlashMessage';
import API from '../services/api';
import { FormSelect } from '../components/FormSelect';

const AdminAdvertisementForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    link_url: '',
    display_type: 'all_locations',
    location_ids: [],
    is_active: true
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    loadLocations();
    if (id) {
      loadAdvertisement();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadLocations = async () => {
    try {
      const res = await API.get('/admin/locations?limit=1000');
      if (res.data.success) {
        // API returns data as an array directly, not nested in locations
        setLocations(Array.isArray(res.data.data) ? res.data.data : []);
      }
    } catch (error) {
      showDanger('Lokasyonlar yüklenirken hata: ' + error.message);
    }
  };

  const loadAdvertisement = async () => {
    try {
      setLoading(true);
      const res = await API.get(`/advertisements/${id}`);
      if (res.data.success) {
        const ad = res.data.data;
        setFormData({
          title: ad.title,
          link_url: ad.link_url || '',
          display_type: ad.display_type,
          location_ids: ad.location_ids || [],
          is_active: ad.is_active === 1
        });
        setImagePreview(ad.image_url);
      }
    } catch (error) {
      showDanger('Reklam yüklenirken hata: ' + error.message);
      navigate('/admin/advertisements');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLocationToggle = (locationId) => {
    setFormData(prev => ({
      ...prev,
      location_ids: prev.location_ids.includes(locationId)
        ? prev.location_ids.filter(locId => locId !== locationId)
        : [...prev.location_ids, locationId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!id && !imageFile) {
      showDanger('Lütfen bir görsel seçin');
      return;
    }

    if (formData.display_type === 'specific_locations' && formData.location_ids.length === 0) {
      showDanger('Lütfen en az bir lokasyon seçin');
      return;
    }

    try {
      setLoading(true);
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('link_url', formData.link_url);
      submitData.append('display_type', formData.display_type);
      submitData.append('is_active', formData.is_active ? 1 : 0);

      if (formData.display_type === 'specific_locations') {
        submitData.append('location_ids', JSON.stringify(formData.location_ids));
      }

      if (imageFile) {
        submitData.append('image', imageFile);
      }

      if (id) {
        await API.put(`/advertisements/${id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showSuccess('Reklam başarıyla güncellendi');
      } else {
        await API.post('/advertisements', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showSuccess('Reklam başarıyla oluşturuldu');
      }

      navigate('/admin/advertisements');
    } catch (error) {
      showDanger('İşlem başarısız: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7a2c2c]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <FlashContainer />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {id ? 'Reklam Düzenle' : 'Yeni Reklam Ekle'}
          </h1>
          <p className="text-gray-600 mt-1">
            {id ? 'Mevcut reklamı düzenleyin' : 'Müşteri sayfalarında gösterilecek yeni reklam oluşturun'}
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/advertisements')}
          className="btn-secondary"
        >
          ← Geri Dön
        </button>
      </div>

      {/* Form */}
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Reklam Başlığı */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reklam Başlığı <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="form-input"
                placeholder="Reklam başlığını giriniz"
                required
                disabled={loading}
              />
            </div>

            {/* Link URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link URL (Opsiyonel)
              </label>
              <input
                type="url"
                value={formData.link_url}
                onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                className="form-input"
                placeholder="https://example.com"
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500">Reklama tıklandığında yönlendirilecek URL</p>
            </div>

            {/* Reklam Görseli */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reklam Görseli <span className="text-red-500">*</span>
                {id && <span className="text-gray-500 font-normal"> (Değiştirmek için yeni görsel seçin)</span>}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7a2c2c] focus:border-[#7a2c2c] file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-[#7a2c2c] hover:file:bg-red-100 transition-colors cursor-pointer"
                required={!id}
                disabled={loading}
              />
              {imagePreview && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mb-2">Önizleme:</p>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full max-w-2xl h-64 object-contain rounded-lg border border-gray-200 shadow-sm bg-gray-50"
                  />
                </div>
              )}
            </div>

            {/* Gösterim Yeri */}
            <div>
              <FormSelect
                name="display_type"
                label="Gösterim Yeri"
                value={formData.display_type}
                onChange={(e) => setFormData({ ...formData, display_type: e.target.value, location_ids: [] })}
                options={[
                  { value: 'all_locations', label: 'Tüm Lokasyonlarda Göster' },
                  { value: 'specific_locations', label: 'Seçili Lokasyonlarda Göster' }
                ]}
                disabled={loading}
              />
            </div>

            {/* Lokasyon Seçimi */}
            {formData.display_type === 'specific_locations' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lokasyon Seçimi <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto border border-gray-300 rounded-lg p-4 bg-gray-50">
                  {locations.length > 0 ? (
                    locations.map(location => (
                      <label
                        key={location.id}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all border ${
                          formData.location_ids.includes(location.id)
                            ? 'bg-red-50 border-red-300 shadow-sm'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={formData.location_ids.includes(location.id)}
                            onChange={() => handleLocationToggle(location.id)}
                            className="h-4 w-4 text-[#7a2c2c] focus:ring-[#7a2c2c] border-gray-300 rounded"
                            disabled={loading}
                          />
                          <span className="text-sm font-medium text-gray-700">{location.name}</span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
                          location.ad_enabled
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {location.ad_enabled ? (
                            <><Check className="w-3 h-3" /> Aktif</>
                          ) : (
                            <><X className="w-3 h-3" /> Pasif</>
                          )}
                        </span>
                      </label>
                    ))
                  ) : (
                    <div className="col-span-2 text-center text-gray-500 py-4">
                      Lokasyon bulunamadı
                    </div>
                  )}
                </div>
                {formData.location_ids.length > 0 && (
                  <p className="mt-2 text-sm text-[#7a2c2c] flex items-center gap-1">
                    <Check className="w-4 h-4" /> {formData.location_ids.length} lokasyon seçildi
                  </p>
                )}
                {formData.location_ids.length === 0 && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> En az bir lokasyon seçmelisiniz
                  </p>
                )}
              </div>
            )}

            {/* Aktif Durumu */}
            <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-5 w-5 text-[#7a2c2c] focus:ring-[#7a2c2c] border-gray-300 rounded"
                disabled={loading}
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                Reklamı aktif olarak yayınla
              </label>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
              <button
                type="submit"
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                disabled={loading}
              >
                {loading ? 'İşleniyor...' : id ? (
                  <><Save className="w-4 h-4" /> Güncelle</>
                ) : (
                  <><Plus className="w-4 h-4" /> Oluştur</>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/advertisements')}
                className="flex-1 sm:flex-none bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors font-medium shadow-sm disabled:opacity-50"
                disabled={loading}
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminAdvertisementForm;
